import { supabase } from "@/integrations/supabase/client";
import type {
	ActiveUnipileAccount,
	InboxConversation,
	InboxMessage,
	InboxParticipant,
	ParticipantDetails,
} from "@/types/inbox";

// Thin fetchers around the existing `unipile-handler` / `unipile-manager`
// edge-function commands. No backend changes — same commands the old
// MailboxDrawer invoked, extracted so react-query owns caching and retries.

class InboxApiError extends Error {}

function assertOk(error: unknown, data: unknown): void {
	if (error) {
		throw error instanceof Error ? error : new InboxApiError(String(error));
	}
	const dataError = (data as { error?: string } | null)?.error;
	if (dataError) throw new InboxApiError(dataError);
}

export async function listConversations(): Promise<InboxConversation[]> {
	const { data, error } = await supabase.functions.invoke(
		"unipile-handler?command=list-conversations",
		{ method: "GET" }
	);
	assertOk(error, data);
	return Array.isArray(data?.conversations) ? data.conversations : [];
}

/**
 * Conversations linked to a CRM entity (candidate/job/company/match), each
 * with its messages embedded — one round trip. Despite the command name the
 * response's `messages` field holds conversations.
 */
export async function listMessagesForEntity(
	entityType: string,
	entityId: string
): Promise<InboxConversation[]> {
	const { data, error } = await supabase.functions.invoke(
		`unipile-handler?command=list-messages-for-entity&entity_type=${entityType}&entity_id=${entityId}`,
		{ method: "GET" }
	);
	assertOk(error, data);
	if (Array.isArray(data)) return data;
	return Array.isArray(data?.messages) ? data.messages : [];
}

export async function getMessages(conversation: {
	id: string;
	source_account_id?: string;
	source_account_type?: string;
}): Promise<InboxMessage[]> {
	let url = `unipile-handler?command=get-conversation-messages&conversation_id=${conversation.id}`;
	if (conversation.source_account_id) url += `&account_id=${conversation.source_account_id}`;
	if (conversation.source_account_type) url += `&type=${conversation.source_account_type}`;
	const { data, error } = await supabase.functions.invoke(url, { method: "GET" });
	assertOk(error, data);
	return Array.isArray(data?.messages) ? data.messages : [];
}

export async function getParticipants(conversationId: string): Promise<InboxParticipant[]> {
	const { data, error } = await supabase.functions.invoke(
		`unipile-handler?command=get-conversation-participants&conversation_id=${conversationId}`,
		{ method: "GET" }
	);
	assertOk(error, data);
	const participants = data?.participants;
	if (participants && Array.isArray(participants.items)) return participants.items;
	if (Array.isArray(participants)) return participants;
	return [];
}

export async function getParticipantDetails(participantId: string): Promise<ParticipantDetails> {
	const { data, error } = await supabase.functions.invoke(
		`unipile-handler?command=get-participant-details&participant_id=${participantId}`,
		{ method: "GET" }
	);
	assertOk(error, data);
	const participant = data?.participant || {};

	let pictureUrl: string | null = participant.picture_url || null;
	if (!pictureUrl) {
		try {
			const { data: pictureData } = await supabase.functions.invoke(
				`unipile-handler?command=get-participant-picture&participant_id=${participantId}`,
				{ method: "GET" }
			);
			pictureUrl = pictureData?.picture || null;
		} catch {
			pictureUrl = null;
		}
	}

	return {
		id: participantId,
		name: participant.name,
		displayName: participant.displayName,
		email: participant.email,
		pictureUrl,
		profileUrl: participant.profile_url || null,
		candidateLinkKey: participant.candidate_link_key || null,
	};
}

export interface SendMessagePayload {
	conversationId: string;
	message: string;
	conversationType?: string;
	isEmailConversation: boolean;
	accountId?: string;
	accountType?: string;
}

export async function sendMessage(payload: SendMessagePayload): Promise<void> {
	const { data, error } = await supabase.functions.invoke(
		`unipile-handler?command=send-message&conversation_id=${payload.conversationId}`,
		{
			method: "POST",
			body: {
				message: payload.message,
				options: {
					conversationType: payload.conversationType,
					isEmailConversation: payload.isEmailConversation,
					accountId: payload.accountId,
					accountType: payload.accountType,
				},
			},
		}
	);
	assertOk(error, data);
}

export async function refreshParticipantPicture(participantId: string): Promise<string | null> {
	const { data, error } = await supabase.functions.invoke(
		`unipile-handler?command=refresh-participant-picture&participant_id=${participantId}`,
		{ method: "GET" }
	);
	assertOk(error, data);
	return data?.picture_url || null;
}

export async function getLinkedInProfile(participantId: string): Promise<unknown> {
	const { data, error } = await supabase.functions.invoke(
		`unipile-handler?command=get-linkedin-profile&participant_id=${participantId}`,
		{ method: "GET" }
	);
	assertOk(error, data);
	if (!data) throw new InboxApiError("No data returned from edge function");
	return data;
}

export async function getActiveAccounts(): Promise<ActiveUnipileAccount[]> {
	const { data, error } = await supabase.functions.invoke(
		"unipile-manager?command=get-active-accounts-for-user",
		{ method: "GET" }
	);
	assertOk(error, data);
	return Array.isArray(data) ? data.filter((account) => account.active === true) : [];
}
